
class FileUploadManager {
  constructor(venueManagerId, API_ENDPOINTS) {
    this.venueManagerId = venueManagerId;
    this.API_ENDPOINTS = API_ENDPOINTS;
    this.filekeyId = `venue/${venueManagerId}/`;
    
    this.keyFilesSufix = {
      "venueImages": "venue_image",
      "wineList": "wine_list",
      "sommelierPicture": "sommelier_image",
      "venue_images[]": "venue_image",
      "wine_list[]": "wine_list",
      "sommelier_image": "sommelier_image"
    };

    this.fileReferenceKeys = {
      "venueImages": "venueImages",
      "wineList": "wineList",
      "sommelierPicture": "sommelierPicture",
      "venue_images[]": "venueImages",
      "wine_list[]": "wineList",
      "sommelier_image": "sommelierPicture"
    };
  }

  getFileExtension(file) {
    if (!file || !file.name) return '';
    return file.name.split('.').pop().toLowerCase();
  }

   isNameless(file) {
    return !file.name || file.name.toLowerCase() === "blob" || !file.name.includes(".");
  }

  getFileSuffix(key) {
    return this.keyFilesSufix[key];
  }

  getFileReferenceKey(key) {
    return this.fileReferenceKeys[key] || key;
  }

  async uploadFiles(submissionFormData) {
    // Get presigned URL
    const responsePresignedUrl = await fetch(this.API_ENDPOINTS.getPresignedUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        keyIdentifier: this.filekeyId
      })
    });

    if (!responsePresignedUrl.ok) {
      throw new Error("Couldn't get presigned URL");
    }

    const { post, key: postKey } = await responsePresignedUrl.json();

    if (!post || !postKey) {
      throw new Error("Couldn't generate post signed url");
    }


    const fileUploads = [];
    
    // Process files
    for (const [key, value] of submissionFormData.entries()) {
      const fileSuffix = this.getFileSuffix(key);
      if ((value instanceof File || value instanceof Blob) && fileSuffix) {
        let f = value;
        let ext = this.getFileExtension(f);
        
        if (this.isNameless(f)) {
          ext = (value.type?.split('/')[1] || 'bin');
          const baseName = `${Math.random().toString(36).slice(2,18)}_${fileSuffix}.${ext}`;
          f = new File([f], baseName, { type: f.type || 'application/octet-stream' });
        }
        const customFileName = `${this.filekeyId}${Math.random().toString(36).substring(2, 18)}_${fileSuffix}.${ext}`;
        
        const filesSubmissionFormData = new FormData();
        Object.entries(post.fields).forEach(([k, v]) => filesSubmissionFormData.append(k, v));
        filesSubmissionFormData.append("file", f, customFileName);
        
        fileUploads.push({
          key: this.getFileReferenceKey(key),
          fileName: f.name,
          customFileName,
          promise: fetch(post.url, {
            method: "POST",
            body: filesSubmissionFormData
          })
        });
      }
    }

    if (fileUploads.length === 0) {
      return { successfulUploads: [], failedUploads: [], cleanSubmissionFormData: submissionFormData };
    }

    // Wait for all uploads and get results
    const uploadResults = await Promise.allSettled(fileUploads.map(item => item.promise));

    // Process results
    const successfulUploads = [];
    const failedUploads = [];

    uploadResults.forEach((result, index) => {
      const fileInfo = fileUploads[index];
      const isFulfilled = result.status === 'fulfilled';
      if (isFulfilled && result.value.ok) {
          successfulUploads.push({
            key: fileInfo.key,
            fileName: fileInfo.customFileName,
            response: result.value,
            status: 'success'
          });
          // console.log(`✅ File ${fileInfo.fileName} uploaded successfully`);
        
      } else {
        failedUploads.push({
            key: fileInfo.key,
            fileName: fileInfo.customFileName,
            ...(isFulfilled ? {response:  result.value} : {}),
            status: 'failed',
            error: isFulfilled ? `HTTP ${result.value.status}: ${result.value.statusText}` : result.reason.message || 'Network error'
          });
          // console.error(`❌ File ${fileInfo.fileName} upload failed:`, result.value.status, result.value.statusText);
      }
    });

    // Log summary
    // console.log(`�� Upload Summary: ${successfulUploads.length} successful, ${failedUploads.length} failed`);

    return { successfulUploads, failedUploads };
  }

  
  processFormDataAfterUpload(submissionFormData, successfulUploads) {
    const processedFormData = new FormData();
    // Copy non-File entries
    for (const [key, value] of submissionFormData.entries()) {
      if (!(value instanceof File)) {
        processedFormData.append(key, value);
      }
    }
    // Add the uploaded file references
    const filesRefs = {};
    for (const fileUpload of successfulUploads) {
      if (!filesRefs[fileUpload.key]) filesRefs[fileUpload.key] = [];
      filesRefs[fileUpload.key].push(fileUpload.fileName);
    }
    processedFormData.append(`filesRefs`, JSON.stringify(filesRefs));

    return processedFormData;
  }


  async uploadFilesAndProcessData(submissionFormData) {
    const { successfulUploads, failedUploads } = await this.uploadFiles(submissionFormData);

    if (failedUploads.length > 0) {
      return { successfulUploads, failedUploads, processedFormData: null };
    }
    
    const processedFormData = this.processFormDataAfterUpload(submissionFormData, successfulUploads);
    
    return { successfulUploads, failedUploads, processedFormData };
  }
}
